import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { Lead } from '../shared/lead';
import { PostLead } from '../shared/post-lead';
import { FormResponse } from '../shared/form-response';
import { SelectedResponse } from '../shared/selected-response.model';
import { BeEnabledResponse } from '../shared/be-enabled-response';
import { GeneralResponse } from '../shared/general-response';
import { ThankYouQueryString } from '../shared/thank-you-query-string';
import { OfficeStatus } from '../shared/office-status';
import { PremiumQuestions } from '../shared/premium-questions';
import { PremiumAnswers } from '../shared/premium-answers';
import { LandingData } from '../shared/landing-data';
import { ActivatedRoute } from '@angular/router';
import { GetLocationResponse } from '../shared/get-location-response';
import { UntypedFormGroup } from '@angular/forms';
import { AnalyticsService } from './analytics.service';
import { NeustarScore } from '../shared/neustar-score';
import { ConsumerReviewsResponse } from '../shared/consumer-reviews-response';
import { catchError, map, take } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { State } from '../shared/state';
import { StateRateDescription } from '../shared/state-rate-description.model';
import { PageInfo } from '../shared/page-info';
import { Rate } from '../shared/rate.model';
import { query } from '@angular/animations';

@Injectable()
export class FruHttpService {
  private wwwUrl: string;
  private adminUrl: string;
  private apiUrl: string;
  private officeStatus: Observable<OfficeStatus>;
  private lenderPhoneStatus: Observable<OfficeStatus>;
  private rateTableUrl: string;

  constructor(
    private http: HttpClient,
    private activatedRoute: ActivatedRoute,
    private analyticsService: AnalyticsService,
    private appConfig: AppConfigService,
  ) {
    this.wwwUrl = appConfig.wwwUrl;
    this.adminUrl = appConfig.adminUrl;
    this.rateTableUrl = appConfig.rateTableUrl;
    this.apiUrl = appConfig.fruApiUrl;
  }

  getLead(id: string, type: string, adnetwork: string = 'consumer_campaign'): Observable<Lead> {
    let url = '';

    if (type === 'abandonment') {
      url = `${this.wwwUrl}/ng_api/get_abandonment_lead/${id}`;
    } else {
      url = `${this.wwwUrl}/ng_api/get_lead/${id}?adnetwork=${adnetwork}`;
    }

    return this.http.get<Lead>(url);
  }

  getPremiumQuestions(id: string): Observable<PremiumQuestions> {
    return this.http.get<PremiumQuestions>(`${this.wwwUrl}/ng_api/get_premium_questions/${id}`);
  }

  getOfficeStatus(adnetwork: string = 'consumer_campaign'): Observable<OfficeStatus> {
    if (this.officeStatus) {
      return this.officeStatus;
    }
    const url = `${this.apiUrl}/status/office`;
    let params : any = {}
    if (adnetwork) {
      params.adnetwork = adnetwork;
    }
    params.domain = window.location.hostname;
    this.officeStatus = this.http.get<OfficeStatus>(url, { params: params} )
      .pipe(catchError(x => {
        return of({ answer: false, phone: null, phonefancy: null, adnetwork: null });
      }));

    return this.officeStatus;
  }

  getMedian() {
    const url: string = `${this.adminUrl}/public_api/median_price`
    return this.http.get(url);
  }

  getLenderPhone(lender_id: string, adnetwork: string = 'consumer_campaign'): Observable<OfficeStatus> {
    // if (this.lenderPhoneStatus) {
    //  return this.lenderPhoneStatus;
    // }
    const url = `${this.wwwUrl}/ng_api/get_lender_phone/${lender_id}/${adnetwork}`;
    this.lenderPhoneStatus = this.http.get<OfficeStatus>(url);
    return this.lenderPhoneStatus;
  }

  getBEstatus(lenderId: string, state: string = '', loanType: string = 'purchase') {
    let params = new HttpParams()
      .set('l', lenderId)
      .set('LoanPurpose', loanType);
    if (state != '') {
      params = params.append('state', state);
    }

    // deepcode ignore Ssrf: this method is run from the browser, not the server
    const url = `${this.wwwUrl}/ng_api/be_enabled?` + params.toString();
    // deepcode ignore Ssrf: this method is run from the browser, not the server
    return this.http.get<BeEnabledResponse>(url);
  }

  getLandingPageData(leadType: string, adnetwork: string, pageType: string = 'default') {
    let params = new HttpParams()
      .set('LoanPurpose', leadType)
      .set('adnetwork', adnetwork)
      .set('type', pageType);

    const url = `${this.wwwUrl}/ng_api/get_landing_data?` + params.toString();

    return this.http.get<LandingData>(url);
  }

  submitLead(lead: PostLead): Observable<FormResponse> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/lead_manager/submit`;

    return this.http.post<FormResponse>(url, lead, httpOptions);
  }

  submitConsent(lead: PostLead): Observable<FormResponse> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/onetoone`;

    return this.http.post<FormResponse>(url, lead, httpOptions);
  }

  bestLender(bestId): Observable<any> {
    const url = `${this.wwwUrl}/ng_api/get_lender/${bestId}`;
    return this.http.get(url);
  }

  saveNotSubmitedLead(lead: PostLead, is_checked: number = 1): Observable<SelectedResponse> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/submit?checked=${is_checked}`;

    return this.http.post(url, lead, httpOptions);
  }

  updateNotSubmitedLead(lead: PostLead): any {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/update`;

    return this.http.post(url, lead, httpOptions);
  }

  getDefaultLenders(lead: PostLead, is_checked: number = 1): Observable<SelectedResponse> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/by_state?checked=${is_checked}`;

    return this.http.post(url, lead, httpOptions);
  }

  setRocketPromptAnswer(clientId, nsLeadId, type, answer): any {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/prompt_answer`;
    let params = {nsLeadId: nsLeadId, rocket_client: clientId, prompt_type: type, answer: answer};

    return this.http.post(url, JSON.stringify(params), httpOptions);
  }

  setAllPromptAnswer(lenders, nsLeadId, type, answer): any {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/selected_manager/prompt_answer`;
    let params = {nsLeadId: nsLeadId, lenders: lenders, prompt_type: type, answer: answer};

    return this.http.post(url, JSON.stringify(params), httpOptions);
  }

  submitPremiumAnswers(premiumAnswers: PremiumAnswers): Observable<GeneralResponse> {
    return this.http.post<GeneralResponse>(`${this.wwwUrl}/ng_api/submit_premium_answers`, premiumAnswers, {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' })
    });
  }

  redirectThankYou(lead: Lead, response: FormResponse, qr: boolean = false) {
    console.log('redirectThankYou', lead, response);

    let qs = this.generateThankYouQueryString({
      leadType: lead.type,
      cashOut: lead.cashOut,
      creditRating: lead.creditRating,
      product: response.product,
      leadId: response.lead_id,
      dc: response.dc || 0,
      im: response.im || 0,
      pth: response.pth || 0,
      prompt: response.prompt || 0,
      sac: response.sac,
      dnt: Number(qr),
      conversionType: response.conversion_type || '',
    });

    const path = qr ? '/thank-you-qr' : '/thank-you/';
    this.analyticsService.trackRedirect(path);
    window.location.href = this.wwwUrl + path + '?' + qs;
  }

  redirectThankYouNoLead(data: object) {
    // for premium questions
    let qs = this.generateThankYouQueryString(data);

    window.location.href = `${this.wwwUrl}/thank-you/?${qs}`;
  }

  generateThankYouQueryString(data: any): string {
    let query: ThankYouQueryString = {
      LoanPurpose: data.leadType || '',
      lc_referrer: 'FreeRateUpdate',
      cash_out: data.cashOut || '',
      credit_rating: data.creditRating || '',
      product_type: data.product || '',
      form: 'web',
      exclusive: 'multiple banks',
      form_version: 1,
      lead_id: data.leadId || '',
      dc: data.dc || 0,
      im: data.im || 0,
      pth: data.pth || 0,
      prompt: data.prompt || 0,
      sac: data.sac || 0,
      dnt: data.dnt || 0,
      conversionType: data.conversionType || '',
    };

    if (query.LoanPurpose != 'purchase' && query.LoanPurpose != 'refinance') {
      query.LoanPurpose = 'refinance'; //switch back LowRateFinder's types to be correct
    }

    let params = new HttpParams();

    for (let key in query) {
      params = params.set(key, query[key]);
    }

    return params.toString();
  }

  redirectMainForm() {
    window.location.href = `${this.wwwUrl}/mortgagerates/index.php?utm_source=consumer_campaign&utm_medium=email_form_redirect`;
  }
 
  /**
   * Redirect to the form with the specified type, and pass existing url params
   * @param type loan type
   */
  redirectForm(type: string): void {
    let qs: HttpParams = new HttpParams();
    for (let key in this.activatedRoute.snapshot.queryParams) {
      qs = qs.set(key, this.activatedRoute.snapshot.queryParams[key]);
    }
    qs = qs.set('LoanPurpose', type);

    let url: string = `${this.wwwUrl}/mortgagerates/final-step.php?${qs}`;
    window.location.href = url;
  }

  adDisclosurePopup(): void {
    this.analyticsService.trackGoal(63);
    let type = 'default';

    if (this.activatedRoute.snapshot.queryParams['type']) {
      type = this.activatedRoute.snapshot.queryParams['type'];
    }

    window.open(`${this.wwwUrl}/advertising_disclosure.php?type=${type}`, 'name', 'scrollbars=1,height=500,width=500');
  }

  licensingDisclosurePopup(): void {
    this.analyticsService.trackGoal(97);

    window.open(`${this.wwwUrl}/licensing_disclosure.php`, 'name', 'scrollbars=1,height=500,width=500');
  }

  getZipDetails(zipCode: string, type: string, creditRating: string): Observable<GetLocationResponse> {
    const qs = new HttpParams()
      .set('zip', zipCode)
      .set('type', type === 'personalloan' ? 'refinance' :type)
      .set('credit', creditRating === 'Unknown' ? 'good' : creditRating.toLowerCase());

    const url = `${this.apiUrl}/status/location?${qs}`;

    return this.http.get<GetLocationResponse>(url).pipe(
      take(1),
      catchError(x => {
        return of({ state:null, city: null, enabled: null,  lenderAvailable: null})
      }));
  }

  /**
   * Redirect consumer to rate tables page
   */
  // redirectRateTables(leadForm: UntypedFormGroup): void {
  //  let params = new HttpParams()
  //    .set('type', leadForm.controls.type && leadForm.controls.type.value || '')
  //    .set('state', leadForm.controls.propertyState && leadForm.controls.propertyState.value || '');
  //  const path = '/mortgage-refinance-purchase-fha-va-jumbo-offers';
  //  this.analyticsService.trackRedirect(path);
  //  document.location.href = path + '?' + params.toString();
  // }

  /**
   * Redirect consumer to other rate tables page
   */
  redirectOffers(leadForm: Lead) {
    let params = new HttpParams();
    if (typeof leadForm !== 'undefined') {
      /*If we’re redirecting a poor credit, but they had set a cash out of 1,000 - 50,000,
      mortgage-refinance-offers?personal-loan=1&cash_out=###&loan_amount=###&property_type=###&loan_type={refinance||purchase} */
      if (leadForm.cashOut >= 1000) {
        params = params
          .set('personal-loan', '1')
          .set('cash_out', leadForm.cashOut?.toString() || '')
          .set('loan_amount', leadForm.desiredLoanAmount?.toString() || '')
          .set('property_type', leadForm.propertyType || '')
          .set('loan_type', leadForm.type || '')
          .set('credit_rating', leadForm.creditRating || '')
          .set('is_own', leadForm.housingSituation || '0');
      } else {
        params = params
          .set('type', leadForm.type || '')
          .set('cash_out', leadForm.cashOut?.toString() || '')
          .set('state', leadForm.propertyState || '');
      }
    }
    params.set('fru_source', 'mortgage-refinance-offers');

    const path = '/mortgage-refinance-offers';
    this.analyticsService.trackRedirect(path);
    document.location.href = path + '?' + params.toString();
  }

  /**
   * Redirect consumer to other rate tables page
   */
  redirectOffersPurchase(leadForm: Lead) {
    let params = new HttpParams();
    if (typeof leadForm !== 'undefined') {
      params = params.set('lead_id', leadForm.leadId || '');
    }
    const path = '/mortgage-purchase-offers'
    this.analyticsService.trackRedirect(path);
    document.location.href = path + '?' + params.toString();
  }

  getState() {
    const url: string = `${this.adminUrl}/public_api/ip_state`
    return new Promise((resolve, reject) => {
      this.http.get(url).toPromise()
        .then((body) => { resolve(body['state'] || ''); })
        .catch(() => { resolve(''); })
    });
  }

  getStateNew() {
    const url: string = `${this.adminUrl}/public_api/ip_state`
    return this.http.get<any>(url);
  }

  getLandingPageState() {
    const url: string = `${this.adminUrl}/public_api/ip_state`;
    return this.http.get(url);
  }

  /**
   * Check Neustar score
   */

   getNeustarScore(lead: PostLead): Observable<NeustarScore> {
    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }
    const url = `${this.adminUrl}/lead_manager/get_neustar_score`;

    return this.http.post<NeustarScore>(url, lead, httpOptions);
  }

  /**
   * getConsumersReviews for ab test
   */

  getConsumersReviews(id) {
    const url: string = `${this.adminUrl}/public_api/get_top_consumer_reviews/${id}`
    return this.http.get<ConsumerReviewsResponse>(url).pipe(map(x => x.data));
  }

  getStatesList() {
    const url: string = `${this.adminUrl}/public_api/get_states_list`
    return this.http.get<{ status: boolean, data: State[] }>(url).pipe(map(x => x.data));
  }

  getStateRateDescription(state: string): Observable<StateRateDescription | null> {

    const url = `${this.apiUrl}/state_rate_description/${state}`;
    return this.http.get<StateRateDescription>(url).pipe(
      catchError(x => {
        console.log('%c State Rate Description Error', 'color:aqua', x);
        return of(null);
      }))
  }

  getPageInfo(page = 'default'): Observable<PageInfo> {
    const url = `${this.apiUrl}/product_info/${page}`;
    return this.http.get<PageInfo>(url).pipe(
      catchError(x => {
        return of(null);
      }))
  }

  getRateFinderRates(): Observable<Rate> {
    return this.http.get<Rate>(`${this.apiUrl}/rate_finder_rates`).pipe(
      catchError(x => {
        console.error('error in /rate_finder_rates');
        return of(null);
      }))
  }

  getPolicyHtml(key: string = "privacy_policy_html", siteId: number = 1): Observable<string> {
    const url: string = `${this.adminUrl}/public_api/get_site_content/${key}/${siteId}`;
    return this.http.get(url, { responseType: 'text' });
  }

  doNotSell(formData: any): Observable<any> {

    const httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    }

    let params = new HttpParams();
    for (const key in formData) {
      if (formData.hasOwnProperty(key)) {
        params = params.set(key, formData[key]);
      }
    }

    const url: string = `${this.adminUrl}/public_api/do_not_sell_ng`;
    return this.http.post<{ status: string, message: string }>(url, params.toString(), httpOptions);
  }

  saveResponse(text: string, step: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/lrf/save_response`, { text: text, step: step });
  }
}
